import { useState } from 'react';
import {
  IoAddOutline,
  IoTrashOutline,
  IoSaveOutline,
  IoArrowForwardOutline,
  IoMoonOutline,
  IoSunnyOutline,
  IoSearchOutline,
  IoMailOutline,
  IoLockClosedOutline,
  IoCheckmarkCircleOutline,
  IoCloseCircleOutline,
  IoFlashOutline,
  IoSparklesOutline,
} from 'react-icons/io5';
import { Button, Input, Badge, Card, CardHeader, CardBody, CardFooter, toggleTheme, getTheme } from '../design';

// Демо-страница новой дизайн-системы.
// Доступна по /design — пока существует только для approval-этапа.
export function DesignSystemPage() {
  const [theme, setTheme] = useState(getTheme());
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');

  function handleToggle() {
    setTheme(toggleTheme());
  }

  function handleAsyncAction() {
    setLoading(true);
    setTimeout(() => setLoading(false), 1800);
  }

  return (
    <div style={{ maxWidth: 960 }}>
      <div className="page-header">
        <div>
          <h2>Design System — preview</h2>
          <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
            Демонстрация нового стиля
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          leftIcon={theme === 'dark' ? <IoSunnyOutline /> : <IoMoonOutline />}
          onClick={handleToggle}
        >
          {theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        </Button>
      </div>

      <Section title="Buttons — варианты">
        <Row>
          <Button variant="primary">Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
        </Row>
      </Section>

      <Section title="Buttons — размеры">
        <Row>
          <Button variant="primary" size="sm">Small</Button>
          <Button variant="primary" size="md">Medium</Button>
          <Button variant="primary" size="lg">Large</Button>
        </Row>
      </Section>

      <Section title="Buttons — с иконками и состояниями">
        <Row>
          <Button variant="primary" leftIcon={<IoAddOutline />}>Создать товар</Button>
          <Button variant="secondary" rightIcon={<IoArrowForwardOutline />}>Перейти</Button>
          <Button variant="danger" leftIcon={<IoTrashOutline />}>Удалить</Button>
          <Button variant="ghost" iconOnly aria-label="Добавить"><IoAddOutline /></Button>
          <Button variant="primary" disabled>Disabled</Button>
          <Button variant="primary" loading>Loading</Button>
          <Button variant="primary" loading={loading} leftIcon={<IoSaveOutline />} onClick={handleAsyncAction}>
            {loading ? 'Сохраняем' : 'Async-клик'}
          </Button>
        </Row>
      </Section>

      <Section title="Inputs">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
          <Input label="Email" type="email" placeholder="you@example.com" leftIcon={<IoMailOutline />}
            value={email} onChange={(e) => setEmail(e.target.value)} fullWidth />
          <Input label="Пароль" type="password" placeholder="••••••••" leftIcon={<IoLockClosedOutline />}
            value={pwd} onChange={(e) => setPwd(e.target.value)} fullWidth />
          <Input label="Поиск" placeholder="Артикул, бренд, модель" leftIcon={<IoSearchOutline />} fullWidth />
          <Input label="Невалидное" defaultValue="abc"
            status="error" hint="Неверный формат" fullWidth />
          <Input label="Успех" defaultValue="OK"
            status="success" hint="Всё хорошо" fullWidth />
          <Input label="Отключено" placeholder="—" disabled fullWidth />
        </div>
      </Section>

      <Section title="Badges">
        <Row>
          <Badge variant="neutral">Нейтрально</Badge>
          <Badge variant="accent">Информация</Badge>
          <Badge variant="success" icon={<IoCheckmarkCircleOutline />}>Принят на складе</Badge>
          <Badge variant="warning" icon={<IoFlashOutline />}>Срочно</Badge>
          <Badge variant="danger" icon={<IoCloseCircleOutline />}>Ошибка</Badge>
        </Row>
        <Row>
          <Badge variant="neutral" solid>5</Badge>
          <Badge variant="accent" solid>NEW</Badge>
          <Badge variant="success" solid>В работе</Badge>
          <Badge variant="warning" solid>Внимание</Badge>
          <Badge variant="danger" solid>Отказ</Badge>
        </Row>
      </Section>

      <Section title="Cards">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          <Card padding="md" elevation="raised">
            <CardHeader title="Без декора" subtitle="Базовая карточка" />
            <CardBody>Просто содержимое.</CardBody>
          </Card>

          <Card padding="md" elevation="raised" accent="accent">
            <CardHeader
              icon={<IoSparklesOutline />}
              title="AI-инсайт"
              subtitle="С акцентной полоской слева"
              actions={<Button variant="ghost" size="sm">Скрыть</Button>}
            />
            <CardBody>Карточка с акцентом подходит для AI-блоков и приоритетных сообщений.</CardBody>
            <CardFooter>
              <Button variant="primary" size="sm">Принять</Button>
              <Button variant="ghost" size="sm">Позже</Button>
            </CardFooter>
          </Card>

          <Card padding="md" elevation="elevated" interactive accent="success">
            <CardHeader title="Интерактивная" subtitle="Hover для эффекта" />
            <CardBody>Лёгкое поднятие при наведении.</CardBody>
          </Card>

          <Card padding="md" elevation="raised" accent="danger">
            <CardHeader
              title="Критично"
              subtitle="Используйте для красных алёртов"
              actions={<Badge variant="danger" solid>3</Badge>}
            />
            <CardBody>Карточка с danger-акцентом.</CardBody>
          </Card>
        </div>
      </Section>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{
      marginTop: 32,
      padding: 20,
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderRadius: 12,
    }}>
      <h3 style={{
        margin: '0 0 16px',
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--color-text-secondary)',
        textTransform: 'uppercase',
        letterSpacing: 0.6,
      }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>{children}</div>;
}
